import * as React from 'react';
import { COLORS } from '../globalStyle/colors';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import DownloadIcon from '../icons/DownloadIcon';
import { ReportRecipientReport } from './models';
import ReportTableRow from './ReportTableRow';
import { TextDirection } from '../localization/textDirection';
import DataTable from '../layout/DataTable';
import Spacer from '../layout/Spacer';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import LocalDiv from '../localization/LocalDiv';
import { css } from '@emotion/core';
import { btnStyle } from '../forms/styles/button';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setAllSelectedAction } from './actions/setAllSelectedAction';
import { useToggle } from '../core/hooks/useToggle';
import { setSelectedAction } from './actions/setSelectedAction';
import { pathCombine } from '../core/api/module';
import { BaseUrl } from '../core/api/endpoints';
import * as R from 'ramda';
import { lastCommaFirstLocal } from '../project/survey/raters/module';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';


const styles = {
  tableContainer:  css`
    max-height: 422px;
    overflow-y: scroll;
  `,
  allColumnHeader: {
    root: FLEX_ROW,
  },
  button:         css`
    font-size: 13px;
    font-weight: bold;
    text-decoration: none;
    color: ${COLORS.BLUE};

    &:hover { 
      text-decoration: underline;
      cursor: pointer;
    }
  `,
  linkDisabled:       css`
    cursor: default;
    opacity: .6;

    &:hover { 
      text-decoration: none;
      cursor: default;
    }
  `,
  headerButton:     css`
    font-size: 12px;
    font-weight: bold;
    padding: 0;
    border: none;
    color: ${COLORS.DARK_GREY};
    background-color: transparent;

    &:hover { 
      text-decoration: underline;
      cursor: pointer;
    }
  `,
  iconStyle:     css`
    padding-left: 5px;
    font-size: 16px;
  `,
};


type Props = {
  reports: ReportRecipientReport[];
  textDirection: TextDirection;
  downloadAllClicked: () => void;
  hasDeliverPermission: boolean;
  hasDeliverThirdPartyPermission: boolean;
  deliverClicked: (report: ReportRecipientReport) => void;
  deliverClickedTo: (
    reports: ReportRecipientReport[], 
    toParticipant: boolean, 
    firstName: string, 
    lastName: string, 
    email: string,
  ) => void;
  redeliverClicked: (report: ReportRecipientReport) => void;
  downloadClicked: () => void;
  recipientId: number;
  dateRange: number | 0;
  projectIds: number[];
  participantIds: number[];
  lastOnly: boolean;
};


/**
 * ReportList: a component that presents information about the reports to be handled by the report recipient.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportRecipientReportList: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const [allSelected, toggleSelected] = useToggle();

  useEffect(
    () => {
      dispatch(setAllSelectedAction(allSelected));
    },
    [dispatch, allSelected],
  );

  const [sortField, setSortField] = React.useState('available');
  const [sortOrder, setSortOrder] = React.useState('desc');

  const mapReportsToRows = React.useMemo(
    () => {
      const reportClicked = (report: ReportRecipientReport, selected: boolean) => {
        dispatch(setSelectedAction(report, selected));
      };

      const sortVal = (sortField: string, a: ReportRecipientReport, b: ReportRecipientReport) => {
        switch(sortField) {
          case 'name':
            const name1 = lastCommaFirstLocal(props.textDirection, a) || '';
            const name2 = lastCommaFirstLocal(props.textDirection, b) || '';
            return name1.localeCompare(name2, 'en');
          case 'email':
            const email1 = a.email || '';
            const email2 = b.email || '';
            return email1.localeCompare(email2, 'en');
          case 'available':
            const delivery1 =  (a.deliveryDate ?? new Date()).toString();
            const delivery2 =  (b.deliveryDate ?? new Date()).toString();
            return delivery1.localeCompare(delivery2, 'en');
          case 'deliveredTo':
            const delivered1 =  a.deliveryNames || '';
            const delivered2 =  b.deliveryNames || '';
            return delivered1.localeCompare(delivered2, 'en');
         case 'reports':
            const reportType1 = a.isComposite ? Str.Composite : a.isSummary ? Str.Summary: Str.Individual;
            const reportType2 = b.isComposite ? Str.Composite : b.isSummary ? Str.Summary: Str.Individual;
            return reportType1.localeCompare(reportType2, 'en');
          default: return 0;
        }
      };

      const sortedReports = (sortField: string, sortOrder: string) => (
        [...props.reports].sort((a, b) => (
            sortVal(sortField, a, b) * (sortOrder === 'asc' ? 1 : -1)
        ))
      );

      const reportsSorted = sortField === '' ? [...props.reports] : sortedReports(sortField, sortOrder);

      return reportsSorted.map(
        report =>
         <ReportTableRow 
            report={report}
            textDirection={props.textDirection}
            key={`${report.releaseId}|${report.participantId}|${report.reportId}|${report.participantReportId}`}
            hasDeliverPermission={props.hasDeliverPermission}
            hasDeliverThirdPartyPermission={props.hasDeliverThirdPartyPermission}
            deliverClicked={props.deliverClicked}
            deliverClickedTo={props.deliverClickedTo}
            redeliverClicked={props.redeliverClicked}
            downloadClicked={props.downloadClicked}
            reportClicked={reportClicked}
            singleReport={props.reports.length === 1}
            differentLanguages={
              props.reports.length > 1 && !R.all(r => r.languageId === props.reports[0].languageId, props.reports)
            }
          />,
      )
    },
    [
      dispatch,
      props.reports,
      props.textDirection,
      props.hasDeliverPermission,
      props.hasDeliverThirdPartyPermission,
      props.deliverClicked,
      props.deliverClickedTo,
      props.redeliverClicked,
      props.downloadClicked,
      sortField,
      sortOrder,
    ],
  );

  const availableDownload = useMemo(
    () => props.reports.filter(r => r.fileExists).length > 0,
    [props.reports],
  );

  const downloadURL = useMemo(
    () => {
      if (!availableDownload) {
        return '#';
      }

      const baseUrl = pathCombine(BaseUrl, '/report/downloadAll');
      const recipientId = `/${props.recipientId}`; 
      const dateRange = `/${props.dateRange}`; 
      const projectIdStr = `/${props.projectIds.length === 0 ? '0' : props.projectIds.join('|') }`; 
      const participantIdStr = `/${props.participantIds.length === 0 ? '0' : props.participantIds.join('|') }`; 
      const lastOnly = `/${props.lastOnly}`; 

      return baseUrl + recipientId +  dateRange + projectIdStr + participantIdStr + lastOnly;
    },
    [availableDownload, props],
  );

  const requestSort = (key: string) => {
    if (key === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(key);
      if (key === 'available') {
        setSortOrder('desc');
      } else {
        setSortOrder('asc');
      }
    }
  };

  const sortAsc = (key: string) => (
    (sortField ?? '') === key && sortOrder === 'asc'
  );
  
  const sortDesc = (key: string) => (
    (sortField ?? '') === key && sortOrder === 'desc'
  );

  return (
    <LocalDiv css={styles.tableContainer}>
      <DataTable
        key="headers"
        headers={[
          <button
            type="button"
            onClick={() => requestSort('name')}
            css={styles.headerButton}
          >
             <Msg str={Str.Name} />
             {sortAsc('name') && <FontAwesomeIcon icon={faSortUp} css={styles.iconStyle} />}
             {sortDesc('name') && <FontAwesomeIcon icon={faSortDown} css={styles.iconStyle} />}
          </button>,
          <button
            type="button"
            onClick={() => requestSort('email')}
            css={styles.headerButton}
          >
            <Msg str={Str.Email} />
            {sortAsc('email') && <FontAwesomeIcon icon={faSortUp} css={styles.iconStyle} />}
            {sortDesc('email') && <FontAwesomeIcon icon={faSortDown} css={styles.iconStyle} />}
          </button>,          
          <button
            type="button"
            onClick={() => requestSort('available')}
            css={styles.headerButton}
          >
            <Msg str={Str.Available} />
            {sortAsc('available') && <FontAwesomeIcon icon={faSortUp} css={styles.iconStyle} />}
            {sortDesc('available') && <FontAwesomeIcon icon={faSortDown} css={styles.iconStyle} />}
          </button>,
          <button
            type="button"
            onClick={() => requestSort('deliveredTo')}
            css={styles.headerButton}
          >
            <Msg str={Str.DeliveredTo} />
            {sortAsc('deliveredTo') && <FontAwesomeIcon icon={faSortUp} css={styles.iconStyle} />}
            {sortDesc('deliveredTo') && <FontAwesomeIcon icon={faSortDown} css={styles.iconStyle} />}
          </button>,
          <button
            type="button"
            onClick={() => requestSort('reports')}
            css={styles.headerButton}
          >
            <Msg str={Str.Reports} />
            {sortAsc('reports') && <FontAwesomeIcon icon={faSortUp} css={styles.iconStyle} />}
            {sortDesc('reports') && <FontAwesomeIcon icon={faSortDown} css={styles.iconStyle} />}
          </button>,
          <LocalDiv css={styles.allColumnHeader.root}>
            <a 
              href={downloadURL}
              onClick={() => props.downloadAllClicked()}
              css={[btnStyle.invisible, styles.button, !availableDownload && styles.linkDisabled ]}
            >
              <LocalDiv css={FLEX_ROW}>
                <Msg str={Str.All} css={FLEX_COLUMN}/>
                <Spacer sizePx={4} css={FLEX_COLUMN} />
                <DownloadIcon sizePx={14} fill={COLORS.BLUE} css={FLEX_COLUMN} />
              </LocalDiv>         
            </a>
          </LocalDiv>,
          props.reports.length === 1 
          ? <div></div> 
          : <input 
              type='checkbox'
              onClick={toggleSelected}
            />,
        ]}
      >
        {mapReportsToRows}
      </DataTable>
    </LocalDiv>
  );
};

export default ReportRecipientReportList;
